import { motion } from "framer-motion";
import { IMAGES } from "@/assets/images";
import { restaurantInfo } from "@/data/index";
import { RESTAURANT_CONSTANTS, ROUTE_PATHS } from "@/lib/index";
import { Button } from "@/components/ui/button";
import { ChefHat, UtensilsCrossed, Leaf, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
export default function About() {
  return <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.RESTAURANT_INTERIOR_1} alt="Intérieur du restaurant" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/40 to-background" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={springPresets.smooth} className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Notre Histoire
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            L'alliance parfaite entre les saveurs d'Afrique et l'élégance européenne.
          </p>
        </motion.div>
      </section>

      {/* Biography Section */}
      <section className="py-16 md:py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={springPresets.gentle} className="relative aspect-square md:aspect-video lg:aspect-square rounded-3xl overflow-hidden shadow-2xl">
            <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2026726189077680128_WhatsApp Image 2026-02-23 at 22.41.20.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Ingrédients frais" className="w-full h-[600.0000610351562px] object-cover" />
            <div className="absolute inset-0 border-[12px] border-white/10 pointer-events-none" />
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-6">
            <motion.div variants={staggerItem} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-full font-semibold text-sm">
              <ChefHat size={18} />
              <span>Le mot de la Chef</span>
            </motion.div>
            
            <motion.h2 variants={staggerItem} className="text-3xl md:text-4xl font-bold text-primary">
              Mabaga Karine
            </motion.h2>
            
            <motion.p variants={staggerItem} className="text-lg text-muted-foreground leading-relaxed italic">
              "{restaurantInfo.description}"
            </motion.p>

            <motion.div variants={staggerItem} className="space-y-4">
              <p className="text-foreground">
                Chez <span className="font-bold">{RESTAURANT_CONSTANTS.NAME}</span>, nous croyons que la cuisine est un pont entre les cultures. 
                Mabaga Karine met tout son cœur et son savoir-faire dans la création de plats qui racontent une histoire — celle d'une Afrique généreuse et d'une Europe raffinée.
              </p>
              <p className="text-foreground">
                Chaque commande est préparée à la minute avec une attention particulière portée à la fraîcheur des produits. Notre service de commande en ligne exclusif nous permet de nous concentrer sur la qualité exceptionnelle de ce qui arrive dans votre assiette.
              </p>
            </motion.div>

            <motion.div variants={staggerItem} className="pt-4 flex flex-wrap gap-4">
              <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-8 rounded-full shadow-lg">
                <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <MessageCircle size={20} />
                  Commander sur WhatsApp
                </a>
              </Button>
              <Button variant="outline" asChild className="h-12 px-8 rounded-full border-primary text-primary hover:bg-primary/5">
                <Link to={ROUTE_PATHS.MENU} className="flex items-center gap-2">
                  Découvrir le Menu
                  <ArrowRight size={18} />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos Valeurs Fondamentales</h2>
            <div className="w-24 h-1 bg-accent mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[{
            icon: <Leaf className="w-10 h-10 mb-4 text-accent" />,
            title: "Produits Frais",
            desc: "Nous sélectionnons rigoureusement nos ingrédients pour garantir des saveurs vibrantes et authentiques."
          }, {
            icon: <UtensilsCrossed className="w-10 h-10 mb-4 text-accent" />,
            title: "Fait Maison",
            desc: "Toutes nos sauces, marinades et plats sont préparés artisanalement dans notre cuisine à Paris."
          }, {
            icon: <ChefHat className="w-10 h-10 mb-4 text-accent" />,
            title: "Héritage Mixte",
            desc: "Une fusion audacieuse qui respecte les traditions africaines tout en intégrant la finesse européenne."
          }].map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            ...springPresets.gentle,
            delay: index * 0.1
          }} className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl text-center hover:bg-white/10 transition-colors">
                <div className="flex justify-center">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-white/70">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 container mx-auto px-4 text-center">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={springPresets.gentle} className="bg-secondary/30 rounded-[3rem] p-12 md:p-20 border border-primary/5">
          <h2 className="text-3xl md:text-5xl font-bold text-primary mb-6">
            Prêt à goûter l'excellence ?
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
            Rejoignez l'expérience {RESTAURANT_CONSTANTS.NAME} et laissez-vous transporter par nos créations culinaires uniques.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-10 py-7 text-lg shadow-xl hover:shadow-accent/20 transition-all">
            <a href={RESTAURANT_CONSTANTS.WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
              <MessageCircle size={24} />
              Commander Maintenant
            </a>
          </Button>
        </motion.div>
      </section>
    </div>;
}